type UnknownRecord = Record<string, unknown>;

const isPlainObject = (value: unknown): value is UnknownRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Reshapes state that was written by an older release onto the current shape: unknown keys
 * are dropped, missing ones filled from `defaults`, known ones keep their stored value.
 * Nested plain objects are reshaped recursively.
 *
 * Arrays count as values, not as objects to be merged entry by entry — merging them would
 * turn a shortened list back into a padded object with numeric keys.
 *
 * `dataKeyed` names the fields whose object keys are values rather than a fixed set of
 * properties; those are passed through unchanged. Measured against the defaults such a
 * field would find no key it recognises and come back empty, silently dropping the user's
 * data. It applies only at the level it is passed at — the recursive call drops it, so a
 * nested field sharing one of those names is not exempted by accident.
 *
 * @TODO Reconsider the approach. Its weak spot is that the distinction between "shape" and
 * "data" is passed in from outside rather than living in the state's own declaration: add
 * a `Record<…>` field, forget to name it, and its contents disappear on the next start with
 * nothing to notice it by. Three directions, roughly by effort:
 *
 *   1. Versioned migrations. Persist a schema version alongside the state and run named
 *      migration steps on read. More code and discipline, but every shape change states
 *      its intent — and it is the only option that can tell "field is new" apart from
 *      "user cleared it", which a structural merge fundamentally cannot.
 *   2. Parse against a schema (zod or similar) instead of merging. `z.record()` vs
 *      `z.object()` makes the distinction part of the declaration, so it cannot be
 *      forgotten, and the state type can be inferred from the schema instead of written
 *      twice. Rejects wrong types too, not just unknown keys.
 *   3. Give each slice a hand-written reader. No generic reshaper that could guess wrong,
 *      at the price of per-slice boilerplate; migrations are still needed, only spread out.
 */
export const tidy = <T extends object>(
  current: unknown,
  defaults: T,
  dataKeyed?: ReadonlySet<string>,
): T => {
  if (!isPlainObject(current)) return defaults;

  const result: UnknownRecord = {};
  for (const [key, fallback] of Object.entries(defaults as UnknownRecord)) {
    if (!(key in current)) {
      result[key] = fallback;
      continue;
    }
    const value = current[key];
    if (dataKeyed?.has(key)) {
      // Only an outright unusable payload falls back to the default.
      result[key] = isPlainObject(value) ? value : fallback;
      continue;
    }
    result[key] =
      isPlainObject(fallback) && isPlainObject(value) ? tidy(value, fallback) : value;
  }
  return result as T;
};
